import React from 'react';
import { Page, Font, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

import logoCentro from 'img/logo-centro-energia.png';
import { POLICIES, INSTRUMENTS } from 'utils/constants';
import KPIPdf from './KPIPdf';
import robotoBold from './fonts/Roboto-Bold.ttf';
import robotoRegular from './fonts/Roboto-Regular.ttf';
import robotoItalic from './fonts/Roboto-Italic.ttf';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: robotoRegular, fontWeight: 400 },
    { src: robotoBold, fontWeight: 700 },
    { src: robotoItalic, fontStyle: 'italic' },
  ],
});

const brandBlue = '#18578f';
const mutedBlue = '#7189ad';
const border = '#d9e1ef';
const lightBg = '#f4f7fb';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Roboto',
    fontSize: 10,
    lineHeight: 1.45,
    paddingTop: 34,
    paddingRight: 44,
    paddingBottom: 34,
    paddingLeft: 44,
    color: '#17345c',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 22,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: border,
  },
  logo: {
    width: 82,
  },
  headerText: {
    fontSize: 8,
    color: mutedBlue,
    textAlign: 'right',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  hero: {
    marginTop: 12,
    paddingTop: 22,
    paddingRight: 24,
    paddingBottom: 24,
    paddingLeft: 24,
    backgroundColor: lightBg,
    borderLeftWidth: 6,
    borderLeftColor: brandBlue,
  },
  eyebrow: {
    fontSize: 9,
    color: mutedBlue,
    textTransform: 'uppercase',
    marginBottom: 8,
    letterSpacing: 0.7,
  },
  title: {
    fontSize: 25,
    fontWeight: 700,
    color: brandBlue,
    lineHeight: 1.15,
    marginBottom: 10,
  },
  lead: {
    fontSize: 11,
    color: '#31496d',
    maxWidth: 440,
  },
  section: {
    marginTop: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: brandBlue,
    marginBottom: 7,
  },
  paragraph: {
    fontSize: 10,
    color: '#31496d',
    marginBottom: 5,
  },
  listCard: {
    borderWidth: 1,
    borderColor: border,
    paddingTop: 10,
    paddingRight: 12,
    paddingBottom: 10,
    paddingLeft: 12,
    marginBottom: 8,
  },
  empty: {
    color: mutedBlue,
    fontStyle: 'italic',
  },
  item: {
    marginBottom: 5,
    color: '#263c5f',
  },
  itemLabel: {
    fontWeight: 700,
    color: '#17345c',
  },
  pageTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: brandBlue,
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  metaText: {
    fontSize: 8,
    color: mutedBlue,
  },
  descriptionBox: {
    paddingTop: 8,
    paddingRight: 10,
    paddingBottom: 8,
    paddingLeft: 10,
    backgroundColor: lightBg,
    borderLeftWidth: 3,
    borderLeftColor: brandBlue,
    marginBottom: 12,
  },
  chartBox: {
    borderWidth: 1,
    borderColor: border,
    padding: 10,
    marginBottom: 12,
  },
  chart: {
    width: '100%',
    objectFit: 'contain',
  },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 44,
    right: 44,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: border,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 7,
    color: mutedBlue,
  },
});

const fixText = (value = '') => String(value)
  .replace(/Ã³/g, 'ó')
  .replace(/Ã©/g, 'é')
  .replace(/Ã¡/g, 'á')
  .replace(/Ã­/g, 'í')
  .replace(/Ãº/g, 'ú')
  .replace(/Ã±/g, 'ñ')
  .replace(/â‚‚/g, '₂')
  .replace(/â€“/g, '-');

const getSelectedSettings = (escenario) => {
  const policies = [];
  const instruments = [];

  if (!escenario || !escenario.settings) {
    return { policies, instruments };
  }

  Object.entries(escenario.settings)
    .filter((item) => item[1].enabled)
    .forEach(([id, value]) => {
      const policy = POLICIES.find((element) => element.id === id);
      const instrument = INSTRUMENTS.find((element) => element.id === id);
      const selected = {
        ...value,
        label: policy ? policy.label : instrument ? instrument.label : id,
      };

      if (policy) policies.push(selected);
      if (instrument) instruments.push(selected);
    });

  return { policies, instruments };
};

const Header = () => (
  <View style={styles.header} fixed>
    <Image style={styles.logo} src={logoCentro} />
    <Text style={styles.headerText}>Modelo PMR | Centro de Energía</Text>
  </View>
);

const Footer = ({ scenarioName }) => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>{scenarioName}</Text>
    <Text style={styles.footerText} render={({ pageNumber }) => `Página ${pageNumber}`} />
  </View>
);

const SettingsList = ({ title, items, emptyText }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    <View style={styles.listCard}>
      {items.length > 0 ? items.map((item, index) => (
        <Text key={`${title}-${index}`} style={styles.item}>
          <Text style={styles.itemLabel}>{fixText(item.label)}</Text>
          {item.trayectoria ? `: trayectoria ${item.trayectoria}` : ': trayectoria 1'}
        </Text>
      )) : <Text style={styles.empty}>{emptyText}</Text>}
    </View>
  </View>
);

const PdfGenerator = ({
  graphImgs,
  kpis,
  kpiDicts,
  year,
  settings,
  escenario,
}) => {
  const title = escenario ? escenario.name : 'Escenario';
  const { policies, instruments } = getSelectedSettings(escenario);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <Header />
        <View style={styles.hero}>
          <Text style={styles.eyebrow}>Reporte de resultados principales</Text>
          <Text style={styles.title}>{fixText(title)}</Text>
          <Text style={styles.lead}>
            Este reporte resume los principales resultados del escenario seleccionado en el modelo PMR.
            Incluye los instrumentos económicos y medidas de mitigación activas, junto con gráficos e
            indicadores clave para el año de análisis.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Alcance del reporte</Text>
          <Text style={styles.paragraph}>
            Los resultados se presentan para facilitar la comparación de trayectorias de emisiones,
            demanda energética y variables del sector eléctrico. Las cifras corresponden a las salidas
            cargadas en la plataforma para el escenario indicado.
          </Text>
          <Text style={styles.paragraph}>Año de indicadores clave: {year}.</Text>
        </View>

        <SettingsList
          title="Instrumentos económicos"
          items={instruments}
          emptyText="No hay instrumentos económicos seleccionados."
        />
        <SettingsList
          title="Medidas de mitigación"
          items={policies}
          emptyText="No hay medidas de mitigación seleccionadas."
        />
        <Footer scenarioName={fixText(title)} />
      </Page>

      {graphImgs.map((graphImg, index) => (
        <Page key={`graph-${index}`} size="A4" style={styles.page} wrap>
          <Header />
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>Escenario: {fixText(title)}</Text>
            <Text style={styles.metaText}>Año KPI: {year}</Text>
          </View>

          <Text style={styles.pageTitle}>{fixText(settings[index].label)}</Text>
          <View style={styles.descriptionBox}>
            <Text style={styles.paragraph}>
              {settings[index].description
                ? fixText(settings[index].description)
                : `La figura presenta la evolución de ${fixText(settings[index].name || settings[index].label)}.`}
            </Text>
          </View>

          <View style={styles.chartBox} wrap={false}>
            <Image src={graphImg.toDataURL()} style={styles.chart} />
          </View>

          {escenario.name === 'Escenario Base' ? null : (
            <KPIPdf
              key={`kpi-pdf-${settings[index].id}`}
              currentScenario={escenario}
              base={kpis[index][0]}
              mode={settings[index].id}
              newValue={kpis[index][1]}
              kpiDict={kpiDicts ? kpiDicts[index] : {}}
              año={year}
            />
          )}
          <Footer scenarioName={fixText(title)} />
        </Page>
      ))}
    </Document>
  );
};

export default PdfGenerator;
